import React, { useState, useMemo } from "react";
import {
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  ProgressBar,
} from "react-bootstrap";
import Dialog, { DialogProps } from "../Dialog";
import Button from "../Button";
import LoadingIndicator from "../LoadingIndicator";
import usePlayerTalents from "../../hooks/usePlayerTalents";
import {
  TalentType,
  PlayerTalent,
  AvailableTalent,
  getTalentDisplayName,
  getTalentIcon,
  calculateTalentEffect,
} from "../../types/talents";
import TalentUpgradeDialog from "./TalentUpgradeDialog";
import { useAppTranslation } from "../../i18n/useAppTranslation";

interface TalentDialogProps extends Omit<DialogProps, "children"> {
  initialTab?: "owned" | "available";
}

const TalentDialog: React.FC<TalentDialogProps> = ({
  open,
  onClose,
  initialTab = "owned",
  ...dialogProps
}) => {
  const { t } = useAppTranslation(["research", "common"]);
  const [activeTab, setActiveTab] = useState<"owned" | "available">(initialTab);
  const [selectedTalentId, setSelectedTalentId] = useState<TalentType | null>(
    null
  );
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  const {
    availableTalents,
    playerTalents,
    loading,
    upgrading,
    error,
    refreshTalents,
    canUpgradeTalent,
    getTalentById,
    getAvailableTalentById,
  } = usePlayerTalents();

  const handleUpgradeClick = (talentId: TalentType) => {
    setSelectedTalentId(talentId);
    setUpgradeDialogOpen(true);
  };

  const handleUpgradeSuccess = async () => {
    setUpgradeDialogOpen(false);
    setSelectedTalentId(null);
    await refreshTalents();
  };

  const ownedTalents = useMemo(
    () => playerTalents.filter((talent) => talent.level > 0),
    [playerTalents]
  );

  const upgradableTalents = useMemo(
    () => availableTalents.filter((talent) => talent.isAvailable),
    [availableTalents]
  );

  const renderTalentCard = (
    talent: PlayerTalent | AvailableTalent,
    isOwned: boolean = false
  ) => {
    const talentId = talent.talentId;
    const displayName = getTalentDisplayName(talentId);
    const icon = getTalentIcon(talentId);
    const currentLevel = isOwned ? (talent as PlayerTalent).level : 0;
    const maxLevel = isOwned
      ? (talent as PlayerTalent).maxLevel
      : (talent as AvailableTalent).maxLevel;
    const isMaxLevel = currentLevel >= maxLevel;
    const canUpgrade = !isOwned || (isOwned && canUpgradeTalent(talentId));
    const isUpgrading = upgrading === talentId;

    const progressPercent = (currentLevel / maxLevel) * 100;

    const tooltipContent = (
      <div
        style={{
          maxWidth: 280,
          whiteSpace: "normal",
          backgroundColor: "#1a1a1a",
          color: "#ffffff",
          padding: "12px",
          borderRadius: "8px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <div
          style={{
            background: isMaxLevel
              ? "linear-gradient(90deg, #FFD700 0%, #FF8C00 100%)"
              : "linear-gradient(90deg, #00E0FF 0%, #5B86E5 100%)",
            color: isMaxLevel ? "#000" : "#fff",
            padding: "6px 10px",
            borderRadius: 4,
            fontWeight: 700,
            marginBottom: 6,
            textAlign: "center",
          }}
        >
          {displayName}{" "}
          {isMaxLevel && `(${t("research:talentsUi.maxShort")})`}
        </div>
        <div>
          <strong>{t("research:talentsUi.description")}:</strong>{" "}
          {talent.description}
        </div>
        {currentLevel > 0 && (
          <div style={{ marginTop: 6 }}>
            <strong>{t("research:talentsUi.currentEffect")}:</strong>{" "}
            {calculateTalentEffect(talentId, currentLevel)}
          </div>
        )}
        {!isMaxLevel && (
          <div style={{ marginTop: 6 }}>
            <strong>{t("research:talentsUi.nextLevelEffect")}:</strong>{" "}
            {calculateTalentEffect(talentId, currentLevel + 1)}
          </div>
        )}
        <div style={{ marginTop: 6 }}>
          <strong>{t("common:labels.level")}:</strong> {currentLevel}/{maxLevel}
        </div>
        {!isOwned && (talent as AvailableTalent).requirements && (
          <div style={{ marginTop: 6, fontSize: "0.9em", opacity: 0.9 }}>
            <strong>{t("research:talents.requirements")}:</strong>
            {Object.entries((talent as AvailableTalent).requirements).map(
              ([key, value]) => (
                <div key={key}>
                  • {key.replace(/([A-Z])/g, " $1").toLowerCase()}: {value}
                </div>
              )
            )}
          </div>
        )}
      </div>
    );

    return (
      <Col key={talentId} xs={12} md={6} lg={4} className="mb-3">
        <OverlayTrigger overlay={<Tooltip>{tooltipContent}</Tooltip>}>
          <div
            className={`talent-card p-3 border rounded ${
              isMaxLevel
                ? "border-warning"
                : currentLevel > 0
                ? "border-info"
                : "border-secondary"
            }`}
            style={{
              minHeight: 140,
              cursor: "pointer",
              transition: "all 0.3s ease",
              position: "relative",
              backgroundColor: isMaxLevel
                ? "rgba(255, 193, 7, 0.1)"
                : currentLevel > 0
                ? "rgba(13, 202, 240, 0.1)"
                : "rgba(108, 117, 125, 0.1)",
              backgroundImage: isMaxLevel
                ? "linear-gradient(135deg, rgba(255, 193, 7, 0.05) 0%, rgba(255, 193, 7, 0.15) 100%)"
                : currentLevel > 0
                ? "linear-gradient(135deg, rgba(13, 202, 240, 0.05) 0%, rgba(13, 202, 240, 0.15) 100%)"
                : "linear-gradient(135deg, rgba(108, 117, 125, 0.05) 0%, rgba(108, 117, 125, 0.15) 100%)",
              color: "#ffffff",
            }}
          >
            <div className="d-flex align-items-start gap-3">
              <img
                src={icon}
                alt={displayName}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  border: "2px solid rgba(255,255,255,0.2)",
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "/assets/images/talents/default.png";
                }}
              />
              <div className="flex-grow-1">
                <h6 className="mb-1 text-truncate">{displayName}</h6>
                <div className="small text-muted mb-2 text-truncate">
                  {talent.description}
                </div>

                {/* Level Progress */}
                <div className="mb-2">
                  <div className="d-flex justify-content-between small mb-1">
                    <span>
                      {t("common:labels.level")} {currentLevel}/{maxLevel}
                    </span>
                    <span>{Math.round(progressPercent)}%</span>
                  </div>
                  <ProgressBar
                    now={progressPercent}
                    variant={isMaxLevel ? "warning" : "info"}
                    style={{ height: 6 }}
                  />
                </div>

                {/* Effect Display */}
                {currentLevel > 0 && (
                  <div className="small text-success mb-2">
                    <strong>{t("research:talentsUi.effect")}:</strong>{" "}
                    {calculateTalentEffect(talentId, currentLevel)}
                  </div>
                )}

                {/* Action Button */}
                {!isMaxLevel && canUpgrade && (
                  <Button
                    size="small"
                    variant={isOwned ? "primary" : "default"}
                    loading={isUpgrading}
                    disabled={loading || isUpgrading}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpgradeClick(talentId);
                    }}
                    style={{ position: "absolute", bottom: 2, right: 8 }}
                  >
                    {isOwned
                      ? t("common:actions.upgrade")
                      : t("research:talentsUi.unlock")}
                  </Button>
                )}

                {isMaxLevel && (
                  <div
                    className="small text-warning fw-bold"
                    style={{ position: "absolute", bottom: 8, right: 8 }}
                  >
                    {t("research:talentsUi.maxLevel")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </OverlayTrigger>
      </Col>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={t("research:talentsUi.playerTalents")}
        size="xl"
        noFooter
        {...dialogProps}
      >
        <div
          className="talents-container"
          style={{
            backgroundColor: "#1a1a1a",
            color: "#ffffff",
            minHeight: "500px",
            borderRadius: "8px",
            padding: "20px",
          }}
        >
          {/* Tab Navigation */}
          <div className="mb-4">
            <div className="btn-group gap-3 w-100" role="group">
              <Button
                variant={activeTab === "owned" ? "wide-primary" : "wide"}
                onClick={() => setActiveTab("owned")}
                className="bg-100"
              >
                {t("research:talentsUi.myTalents")} ({ownedTalents.length})
              </Button>
              <Button
                variant={activeTab === "available" ? "wide-primary" : "wide"}
                onClick={() => setActiveTab("available")}
                className="bg-100"
              >
                {t("research:talentsUi.available")} ({upgradableTalents.length})
              </Button>
            </div>
          </div>

          {/* Error Message */}
          {error && <div className="alert alert-danger mb-3">{error}</div>}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-5">
              <LoadingIndicator />
              <div className="mt-2">{t("research:talentsUi.loadingTalents")}</div>
            </div>
          )}

          {/* Content */}
          {!loading && (
            <Row className="g-3">
              {activeTab === "owned" && (
                <>
                  {ownedTalents.length > 0 ? (
                    ownedTalents.map((talent) => renderTalentCard(talent, true))
                  ) : (
                    <Col xs={12}>
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-star fa-3x mb-3 opacity-50"></i>
                        <h5>{t("research:talentsUi.noTalentsUnlocked")}</h5>
                        <p>
                          {t("research:talentsUi.unlockFirstTalentHint")}
                        </p>
                      </div>
                    </Col>
                  )}
                </>
              )}

              {activeTab === "available" && (
                <>
                  {upgradableTalents.length > 0 ? (
                    upgradableTalents.map((talent) =>
                      renderTalentCard(talent, false)
                    )
                  ) : (
                    <Col xs={12}>
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-lock fa-3x mb-3 opacity-50"></i>
                        <h5>{t("research:talentsUi.noAvailableTalents")}</h5>
                        <p>{t("research:talentsUi.meetRequirementsHint")}</p>
                      </div>
                    </Col>
                  )}
                </>
              )}
            </Row>
          )}

          {/* Refresh Button */}
          <div className="text-center mt-4">
            <Button
              variant="wide"
              onClick={refreshTalents}
              loading={loading}
              disabled={loading}
            >
              <i className="fas fa-sync-alt me-2"></i>
              {t("research:talentsUi.refresh")}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Upgrade Dialog */}
      {selectedTalentId && (
        <TalentUpgradeDialog
          open={upgradeDialogOpen}
          onClose={() => {
            setUpgradeDialogOpen(false);
            setSelectedTalentId(null);
          }}
          talentId={selectedTalentId}
          onUpgradeSuccess={handleUpgradeSuccess}
        />
      )}
    </>
  );
};

export default TalentDialog;
